import React, { useEffect, useContext } from 'react'
import { Link, Prompt, useHistory } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { Card, CardBody, Progress, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import justcastApi from '../../api/justcast'
import { Context as MenuContext } from '../../context/MenuContext'
import { useBulkUpload, splitPick } from '../../hooks/useBulkUpload'
import { bulkUploadApi, bulkUploadTransport } from '../../api/bulkUpload'

const ITEM_LABEL = {
  pending: 'Waiting…',
  uploading: 'Uploading…',
  done: 'Uploaded',
  error: 'Failed'
}

const ERROR_HEADING = {
  presigning: 'Could not prepare the upload.',
  uploading: 'One or more files could not be uploaded.',
  creating: 'Files uploaded, but the episodes could not be created.'
}

const ErrorReference = ({ error }) => {
  if (!error.status && !error.requestId) return null
  return (
    <div className="small mt-2">
      {error.status && <>HTTP {error.status}</>}
      {error.status && error.requestId && <> · </>}
      {error.requestId && <>Reference: {error.requestId}</>}
    </div>
  )
}

// Render the batch-failure alert. A 402 (quota) — at presign OR create time,
// both authoritative and both leaving nothing persisted — spells out the numbers
// and links to /plans. 422 (Dropbox) / 400 (count) show their readable message.
const BatchError = ({ error }) => {
  if (!error) return null
  if (error.status === 402) {
    return (
      <Alert color="warning" className="mt-3">
        Your plan allows {error.limit} episodes; this upload would exceed it by {error.overage}.
        {' '}No episodes were created. <Link to="/plans">Upgrade your plan</Link>
        <ErrorReference error={error} />
      </Alert>
    )
  }
  if (error.message) {
    return (
      <Alert color="danger" className="mt-3">
        <strong>{ERROR_HEADING[error.stage] || 'Bulk upload failed.'}</strong>
        <div>{error.message}</div>
        <ErrorReference error={error} />
      </Alert>
    )
  }
  return (
    <Alert color="danger" className="mt-3">
      Batch failed. Please refresh and try again.
    </Alert>
  )
}

// Thin rendering of the bulk-upload state machine. No confirm button: picking files starts everything.
const BulkUpload = (props) => {
  const { show_id } = props.match.params
  const { status, items, error, busy, cancellable, start, reset, cancel } = useBulkUpload({
    showId: show_id,
    api: bulkUploadApi,
    transport: bulkUploadTransport
  })

  const history = useHistory()
  const [pickError, setPickError] = React.useState(null)
  const [asDrafts, setAsDrafts] = React.useState(false)
  const { setEpisodesMenus } = useContext(MenuContext)

  // Populate the sidebar/header chrome on direct load (menu persists across SPA nav,
  // so arriving from the Episodes screen already has it — mirrors screens/Seasons.js).
  useEffect(() => {
    justcastApi.get(`/v2/shows/${show_id}`)
      .then((res) => setEpisodesMenus({
        title: 'Bulk Upload',
        subtitle: res.data.podcast_title,
        currentPageId: 'episodes',
        currentTabId: 'episodes',
        showId: show_id,
        landingPageUrl: res.data.landing_page_url,
        websiteUrl: res.data.player_page_link,
        rssFeed: res.data.rss_feed,
        passwordProtected: res.data.is_password_protected,
        advancedPrivate: res.data.is_advanced_private_feed,
        isPrivate: res.data.is_private,
        isPrivateShow: res.data.is_private_show,
        isDirectUpload: res.data.is_direct_upload,
        skill_podcast_website: res.data.skill_podcast_website,
        shopifyApp: res.data.is_shopify_app
      }))
      .catch(() => {})
  }, [show_id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Unmount = the user confirmed the <Prompt> and navigated away (or the route died):
  // cancel the batch so a still-running run can't create episodes in the background.
  useEffect(() => cancel, [cancel])

  // While leaving can still stop episodes being created, warn on tab close/reload.
  // React-router's <Prompt> below covers in-app nav; this covers leaving the site.
  // Both disarm once the create POST is dispatched — committed either way by then.
  useEffect(() => {
    if (!cancellable) return
    const handler = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [cancellable])

  // Unsupported files are skipped (message names them); the good ones still upload.
  const onPick = (files) => {
    const { good, message } = splitPick(files)
    setPickError(message)
    if (good.length) start(good, asDrafts)
  }

  // Accept pre-filters the Browse dialog only; rejected drops are merged back in
  // so validatePick stays the single source of validation messages.
  const { getRootProps, getInputProps } = useDropzone({
    accept: ['.mp3', '.m4a', 'audio/mpeg', 'audio/mp4', 'audio/x-m4a'],
    multiple: true,
    disabled: busy,
    onDrop: (acceptedFiles, rejectedFiles) => {
      const files = [...acceptedFiles, ...rejectedFiles]
      if (files.length) onPick(files)
    }
  })

  // Batch outcome, split for the results modal (upload + create-time failures both
  // land as 'error'; everything created is 'done').
  const failed = items.filter((item) => item.status === 'error')
  const uploaded = items.filter((item) => item.status === 'done')

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12" style={{ margin: '20px 0 60px 0' }}>
          <Card>
            <CardBody>
              <h4 className="card-header-title mb-3">Bulk Upload</h4>
              {/* Must be set before dropping files — there is no confirm step. */}
              <div className="custom-control custom-checkbox mb-3">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="bulk-upload-as-drafts"
                  checked={asDrafts}
                  disabled={busy}
                  onChange={(e) => setAsDrafts(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="bulk-upload-as-drafts">
                  Upload as drafts (episodes won't appear in your RSS feed until published)
                </label>
              </div>
              <div {...getRootProps({ className: 'dropzone' })}>
                <div className="dz-default dz-message">
                  <input {...getInputProps()} />
                  <p>Drag and drop audio files or <span style={{
                    textDecoration: 'underline',
                    fontWeight: 600
                  }}>Browse</span></p>
                </div>
              </div>

              {pickError && (
                <div className="mt-3 text-danger">{pickError}</div>
              )}

              {status === 'creating' && (
                <div className="mt-3 text-muted">Creating episodes…</div>
              )}

              {items.length > 0 && (
                <div className="mt-3">
                  {items.map((item, i) => (
                    <div key={i} className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span className="text-truncate mr-2">{item.name}</span>
                        <small className={item.status === 'error' ? 'text-danger' : 'text-muted'}>
                          {ITEM_LABEL[item.status]}
                        </small>
                      </div>
                      <Progress
                        value={Math.round((item.progress || 0) * 100)}
                        color={item.status === 'error' ? 'danger' : 'primary'}
                      />
                    </div>
                  ))}
                </div>
              )}

              {status === 'error' && <BatchError error={error} />}
            </CardBody>
          </Card>
        </div>
      </div>

      <Prompt
        when={cancellable}
        message="Uploads in progress — leave anyway? Files not yet finished won't become episodes."
      />

      {/* Batch ending: summarize every completed batch. No retry — a creator re-picks
          failed files manually. "Upload more" resets to a fresh, unlocked picker. */}
      <Modal isOpen={status === 'done'}>
        <ModalHeader>Upload complete</ModalHeader>
        <ModalBody>
          <p>{uploaded.length} uploaded, {failed.length} failed.</p>
          {failed.length > 0 && (
            <>
              <p className="mb-1 text-muted">Failed files:</p>
              <ul className="mb-0">
                {failed.map((f, i) => (
                  <li key={i}>
                    {f.name}{f.error ? ` — ${f.error}` : ''}
                  </li>
                ))}
              </ul>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={reset}>Upload more</Button>
          <Button color="primary" onClick={() => history.push(`/shows/${show_id}/episodes`)}>
            View episodes
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default BulkUpload
